import type { BvSiteStatus } from '../types/billValidationTypes';
import { monthEnd } from './billMonths';

export type SiteEnrolment = {
  bill_val_status?: BvSiteStatus;
  enrolment_date?: string | null;
  unenrolment_date?: string | null;
};

/** Enrolment status is stored as a literal and is not maintained by the
 *  enrolment/unenrolment dates, so a site whose unenrolment date has passed
 *  still read as "Enrolled". This reconciles the two for display: the stored
 *  value is never overwritten, but a contradiction is reported.
 *
 *  `asOf` defaults to today and exists so callers can render a period-relative
 *  status (e.g. as at a bill's end date). */
export function effectiveSiteStatus(
  bv: SiteEnrolment | null | undefined,
  asOf: string = new Date().toISOString().slice(0, 10),
): { status: BvSiteStatus; stored: BvSiteStatus; note: string | null } {
  const stored: BvSiteStatus = bv?.bill_val_status ?? 'Not Enrolled';
  const enrol = bv?.enrolment_date ?? null;
  const unenrol = bv?.unenrolment_date ?? null;

  if (unenrol && unenrol < asOf && stored !== 'Cancelled') {
    return {
      status: 'Cancelled',
      stored,
      note: `Unenrolment date ${unenrol} has passed, but the saved status is "${stored}".`,
    };
  }
  if (enrol && enrol > asOf && stored === 'Enrolled') {
    return {
      status: 'Pending Enrolment',
      stored,
      note: `Enrolment date ${enrol} is in the future, but the saved status is "Enrolled".`,
    };
  }
  return { status: stored, stored, note: null };
}

// asOf = monthEnd(month). Expected when effectiveSiteStatus(bv, asOf).status is
// 'Enrolled' | 'Pending Cancellation'. Otherwise expected ONLY when the stored
// status is 'Cancelled', unenrolment_date >= asOf, and (no enrolment_date or
// enrolment_date <= asOf): the stored value is today's, and the unenrolment date
// says the site was still enrolled at the end of this month (spec §6 "cancelled in October").
export function expectedInMonth(bv: SiteEnrolment | null | undefined, month: string): boolean {
  const asOf = monthEnd(month);
  const { status, stored } = effectiveSiteStatus(bv, asOf);
  if (status === 'Enrolled' || status === 'Pending Cancellation') return true;
  const unenrol = bv?.unenrolment_date ?? null;
  const enrol = bv?.enrolment_date ?? null;
  return stored === 'Cancelled' && !!unenrol && unenrol >= asOf && (!enrol || enrol <= asOf);
}

/** Was the site in the service on any day of a bill's own period? Decides
 *  whether a bill counts in the match rate. Judged over the bill, not a month
 *  end, so a site's final bill (period ending on its unenrolment date) counts.
 *  Enrolled / Pending Cancellation: from enrolment_date (if any) to
 *  unenrolment_date (if any). Cancelled: only with an unenrolment_date, which
 *  bounds the window. Pending Enrolment and Not Enrolled never count: the
 *  status is operator-maintained and a passed enrolment date does not promote
 *  it (effectiveSiteStatus does not either). */
export function enrolledDuring(bv: SiteEnrolment | null | undefined, from: string, to: string): boolean {
  const stored = bv?.bill_val_status ?? 'Not Enrolled';
  const enrol = bv?.enrolment_date ?? null;
  const unenrol = bv?.unenrolment_date ?? null;
  if (stored === 'Cancelled' ? !unenrol : stored !== 'Enrolled' && stored !== 'Pending Cancellation') return false;
  return (!enrol || enrol <= to) && (!unenrol || unenrol >= from);
}
